import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Instagram, Facebook, Twitch, Youtube, Music2, Radio } from "lucide-react";
import { LOGO_HORIZONTAL, SOCIALS } from "../lib/brand";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/episodes", label: "Episodes" },
  { to: "/editorial", label: "Editorial" },
  { to: "/listen", label: "Listen" },
  { to: "/media-kit", label: "Media Kit" },
  { to: "/contact", label: "Contact" },
];

const ICONS = { Instagram, Facebook, Twitch, Youtube, Music2, Radio };

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="site-root">
      {/* Top ticker */}
      <div className="bg-[var(--ink)] text-[var(--paper)] py-2 overflow-hidden border-b-2 border-[var(--rust)]">
        <div className="ticker font-mono-ed text-[0.72rem] tracking-widest uppercase">
          {Array(2).fill(0).map((_, i) => (
            <div key={i} className="flex gap-10 shrink-0">
              <span>● New Episode Every Monday</span>
              <span className="text-[var(--rust)]">Raw.</span>
              <span>Draft Board Update — Week 9</span>
              <span className="text-[var(--rust)]">Bold.</span>
              <span>Press Inquiries: press@unfilteredandonball.com</span>
              <span className="text-[var(--rust)]">Unapologetic.</span>
              <span>Watch live on Twitch · @Unfilteredandonball</span>
            </div>
          ))}
        </div>
      </div>

      {/* Masthead */}
      <header className="bg-[var(--paper)] border-b-2 border-[var(--ink)] sticky top-0 z-40" data-testid="site-header">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-home-link">
            <img src={LOGO_HORIZONTAL} alt="Unfiltered & On-Ball"
                 className="h-12 md:h-14 w-auto"
                 loading="eager" />
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="main-nav">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to}
                    data-testid={`nav-link-${n.label.toLowerCase().replace(' ', '-')}`}
                    className={`nav-link ${pathname === n.to ? "active" : ""}`}>
                {n.label}
              </Link>
            ))}
            <Link to="/media-kit" className="btn-rust text-xs py-2 px-4" data-testid="press-cta-header">
              Press Kit
            </Link>
          </nav>

          <button className="md:hidden p-2 border-2 border-[var(--ink)]"
                  onClick={() => setOpen((o) => !o)}
                  data-testid="mobile-menu-toggle"
                  aria-label="Toggle menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t-2 border-[var(--ink)] bg-[var(--paper)]" data-testid="mobile-nav">
            <div className="flex flex-col px-5 py-4 gap-3">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to}
                      className={`nav-link text-base ${pathname === n.to ? "active" : ""}`}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(' ', '-')}`}>
                  {n.label}
                </Link>
              ))}
              <Link to="/media-kit" className="btn-rust text-xs py-2 px-4 self-start mt-2">
                Press Kit
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" data-testid="main-content">{children}</main>

      {/* Footer */}
      <footer className="bg-[var(--ink)] text-[var(--paper)] mt-20" data-testid="site-footer">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="font-display text-3xl md:text-4xl leading-none uppercase">
                Unfiltered<span className="text-[var(--rust)]"> &amp; </span>On-Ball
              </div>
              <p className="mt-3 font-serif-ed italic text-[var(--paper-deep)]/80 max-w-md">
                The sports podcast that refuses to play it safe. Raw takes, real perspective, from two former players who still live the game.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                {SOCIALS.map((s) => {
                  const Ic = ICONS[s.icon] || Music2;
                  return (
                    <a key={s.key} href={s.url} target="_blank" rel="noreferrer"
                       className="w-10 h-10 flex items-center justify-center border-2 border-[var(--paper)]/30 hover:border-[var(--rust)] hover:text-[var(--rust)] transition"
                       data-testid={`footer-social-${s.key}`}
                       aria-label={s.label}>
                      <Ic size={18} />
                    </a>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="font-display uppercase text-sm tracking-widest mb-4 text-[var(--rust)]">Navigate</div>
              <ul className="space-y-2">
                {NAV.map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="hover:text-[var(--rust)] transition">{n.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="font-display uppercase text-sm tracking-widest mb-4 text-[var(--rust)]">Press</div>
              <ul className="space-y-2 text-sm">
                <li>press@unfilteredandonball.com</li>
                <li>Booking &amp; Partnerships</li>
                <li><Link to="/media-kit" className="underline hover:text-[var(--rust)]">Download Media Kit</Link></li>
                <li><a href="/api/rss.xml" target="_blank" rel="noreferrer" className="underline hover:text-[var(--rust)]" data-testid="footer-rss-link">RSS Feed</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-[var(--paper)]/20 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs font-mono-ed uppercase tracking-widest text-[var(--paper-deep)]/60">
            <div>© {new Date().getFullYear()} Unfiltered &amp; On-Ball · All rights reserved</div>
            <div>Sports &amp; Conversation · Est. 2026</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
